import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { HlmBtnDirective, HlmIconComponent, HlmTooltipDirective } from '../../ui';
import { WelcomeBarComponent } from '../../features/welcome-bar/welcome-bar.component';
import { QuoteOfTheDayComponent } from '../../features/quote-of-the-day/quote-of-the-day.component';
import { UnifiedSearchComponent } from '../../features/search/unified/unified-search.component';
import { SearchStateService } from '../../features/search/unified/search-state.service';

/**
 * The app's landing page (EB-3621 Plan 5). Blends the personal greeting + quote-of-the-day
 * with the unified search. The greeting and quote show while the search is idle and disappear
 * the moment the user starts typing (read from the child's `query` signal via the `#us` ref),
 * so the results take over the full width. The search instance itself stays mounted across both
 * states — only the surrounding greeting/quote toggle — so query/results state is never lost.
 *
 * A discreet "Classic search" switch-back button routes to the preserved AG-Grid search
 * (`/classic-search`). The route supplies the `SearchGateway` provider.
 */
@Component({
  selector: 'cmt-landing',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    HlmBtnDirective,
    HlmIconComponent,
    HlmTooltipDirective,
    WelcomeBarComponent,
    QuoteOfTheDayComponent,
    UnifiedSearchComponent,
  ],
  template: `
    <!-- Full-width, left-aligned content like the rest of AMIE. -->
    <div class="flex w-full flex-col gap-6 px-8 py-6">
      <!-- Full-width welcome band (centered), idle-only. -->
      @if (!us.query().trim()) {
        <cmt-welcome-bar align="center" />
      }

      <!-- Below the greeting: search + launchpad (left) beside the daily quote (right column).
           Responsive quote width: full (44rem) on xl, halved (22rem) on lg so it doesn't squeeze
           the search, hidden below lg. When searching, the search takes the full width. -->
      <div [class]="!us.query().trim()
        ? 'grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_22rem] xl:grid-cols-[minmax(0,1fr)_44rem]'
        : 'block'">
        <div class="min-w-0">
          <cmt-unified-search
            #us
            [initialQuery]="searchStateService.landingQuery()"
            (queryChange)="searchStateService.landingQuery.set($event)"
          >
            <a
              searchHeaderAction
              hlmBtn
              variant="outline"
              size="sm"
              routerLink="/classic-search"
              hlmTooltip="Switch to the classic grid search"
              class="shrink-0 gap-2"
            >
              <hlm-icon name="lucideLayoutGrid" size="0.9rem" />
              Classic search
            </a>
          </cmt-unified-search>
        </div>
        @if (!us.query().trim()) {
          <aside class="hidden lg:block">
            <cmt-quote-of-the-day />
          </aside>
        }
      </div>
    </div>
  `,
})
export class LandingComponent {
  /** Persists the landing search so returning here (e.g. browser back) restores it. */
  protected readonly searchStateService = inject(SearchStateService);
}
